import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { authClient } from "@/lib/auth-client";
import type { InvitationPreview } from "@/hooks/use-invitation-preview";
import { queryKeys } from "@/lib/query-keys";

type AcceptInvitationModalProps = {
  open: boolean;
  invitation: InvitationPreview;
  onClose: () => void;
};

export function AcceptInvitationModal({ open, invitation, onClose }: AcceptInvitationModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"accept" | "reject" | null>(null);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response = await authClient.organization.acceptInvitation({
        invitationId: invitation.id,
      });
      if (response.error) {
        throw new Error(response.error.message || "No se pudo aceptar");
      }
      return response.data;
    },
    onSuccess: async () => {
      await authClient.organization.setActive({
        organizationId: invitation.organizationId,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.organizations.list() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.organizations.active() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.organizations.userInvitations() });
      toast.success(`Te uniste a ${invitation.organization.name}`);
      navigate("/");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "No se pudo aceptar la invitación");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const response = await authClient.organization.rejectInvitation({
        invitationId: invitation.id,
      });
      if (response.error) {
        throw new Error(response.error.message || "No se pudo rechazar");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success("Invitación rechazada");
      onClose();
      navigate("/");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "No se pudo rechazar");
    },
  });

  const isPending = acceptMutation.isPending || rejectMutation.isPending;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Invitación a organización</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Querés unirte a <strong>{invitation.organization.name}</strong> como{" "}
            <strong>{invitation.role}</strong>?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={isPending}
            onClick={() => {
              setMode("reject");
              rejectMutation.mutate();
            }}
          >
            {mode === "reject" && isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Rechazar
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() => {
              setMode("accept");
              acceptMutation.mutate();
            }}
          >
            {mode === "accept" && isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Aceptar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
